import * as Sentry from '@sentry/react';

import { DISCOUNT_IDS } from '@constants/apiConstants';
import { MOBILE_TARIFF_API_NAMES } from '@constants/apiConstants';
import { MOBILE_TARIFF_NAMES_FORMATTED } from '@constants/app';
import {
  BasketMobile,
  ChargeAmount,
  MobileTariff,
  MobileTariffType,
  QualifiedDiscount,
  Sim,
} from '@generated/gql/graphql';

import { convertValueToCurrency, subtractPrices } from './price';

const getDiscountDurationMonths = (
  discount: QualifiedDiscount
): number | null => discount.discountLengthMonths ?? null;

const findMultiSimDiscount = (discounts: QualifiedDiscount[]) =>
  discounts.find(({ id }) => id === DISCOUNT_IDS.MULTI_SIM) ?? null;

const findUnlimitedMaxDiscount = (discounts: QualifiedDiscount[]) =>
  discounts.find(
    ({ id }) =>
      id === DISCOUNT_IDS.MULTI_SIM_UNLIMITED_MAX ||
      id === DISCOUNT_IDS.MULTI_SIM_GO_UNLIMITED
  ) ?? null;

const findSimsDiscounts = (sims: Sim[]) => {
  let unlimitedMaxDiscount: Nullable<QualifiedDiscount> = null;
  let multiSimDiscount: Nullable<QualifiedDiscount> = null;

  for (const sim of sims) {
    const maxDiscount = findUnlimitedMaxDiscount(
      sim.selectedTariff.discounts ?? []
    );
    const multiDiscount = findMultiSimDiscount(
      sim.selectedTariff.discounts ?? []
    );

    if (unlimitedMaxDiscount && multiDiscount) {
      break;
    }

    if (!unlimitedMaxDiscount && maxDiscount) {
      unlimitedMaxDiscount = maxDiscount;
    }

    if (!multiSimDiscount && multiDiscount) {
      multiSimDiscount = multiDiscount;
    }
  }

  return {
    unlimitedMaxDiscount,
    multiSimDiscount,
  };
};

interface DiscountedMobileService {
  total: ChargeAmount;
  afterPromoTotal: ChargeAmount;
  durationMonths: number | null;
  hasUnlimitedMaxPromo: boolean;
  hasMultiSimPromo: boolean;
}

const discountedSimTariff = (tariff: MobileTariff) => {
  const { monthlyCost, discounts } = tariff;
  const multi = findMultiSimDiscount(discounts ?? []);
  const max = findUnlimitedMaxDiscount(discounts ?? []);

  // Legacy Unlimited Max always pairs its promo discount with a permanent
  // Multi-Sim discount on the sibling SIM (see discount-api
  // mobileSimDiscount.Evaluate: the sibling always gets both, the anchor
  // gets neither) - so seeing it alone is unexpected. Go Unlimited's promo
  // discount, by contrast, is emitted on its own with no permanent discount
  // to fall back to (its OfferBanner copy never mentions an ongoing
  // discounted price the way Unlimited Max's does).
  if (max && !multi && max.id === DISCOUNT_IDS.MULTI_SIM_UNLIMITED_MAX) {
    Sentry.captureMessage(
      'discountedSimTariff: Unlimited Max discount present without Multi-Sim discount - price may be incorrect'
    );

    return {
      totalPrice: parseFloat(monthlyCost.value),
      afterPromoPrice: parseFloat(monthlyCost.value),
    };
  }

  // Unlimited Max discount is higher, so it takes priority over Multi-Sim for basket price presentation
  if (max && multi) {
    const price = subtractPrices(monthlyCost, max.discountedPrice);
    const afterPrice = subtractPrices(monthlyCost, multi.discountedPrice);

    return {
      totalPrice: parseFloat(price.value),
      afterPromoPrice: parseFloat(afterPrice.value),
    };
  }

  if (max) {
    // Go Unlimited: promo-only discount, reverts to full price afterward.
    const price = subtractPrices(monthlyCost, max.discountedPrice);

    return {
      totalPrice: parseFloat(price.value),
      afterPromoPrice: parseFloat(monthlyCost.value),
    };
  }

  if (multi) {
    const price = subtractPrices(monthlyCost, multi.discountedPrice);

    return {
      totalPrice: parseFloat(price.value),
      afterPromoPrice: parseFloat(monthlyCost.value),
    };
  }

  return {
    totalPrice: parseFloat(monthlyCost.value),
    afterPromoPrice: parseFloat(monthlyCost.value),
  };
};

const discountedMobileService = (
  mobile: BasketMobile
): DiscountedMobileService => {
  const sims = mobile.sims ?? [];
  const { unlimitedMaxDiscount, multiSimDiscount } = findSimsDiscounts(sims);

  let promoDuration: number | null = null;

  if (unlimitedMaxDiscount) {
    promoDuration = getDiscountDurationMonths(unlimitedMaxDiscount);
  }

  const { totalPrice, afterPromoPrice } = sims.reduce(
    ({ totalPrice, afterPromoPrice }, sim) => {
      const { totalPrice: simTotalPrice, afterPromoPrice: simAfterPromoPrice } =
        discountedSimTariff(sim.selectedTariff);

      return {
        totalPrice: totalPrice + simTotalPrice,
        afterPromoPrice: afterPromoPrice + simAfterPromoPrice,
      };
    },
    { totalPrice: 0, afterPromoPrice: 0 }
  );

  return {
    total: convertValueToCurrency(totalPrice),
    afterPromoTotal: convertValueToCurrency(afterPromoPrice),
    durationMonths: promoDuration,
    hasUnlimitedMaxPromo: !!unlimitedMaxDiscount,
    hasMultiSimPromo: !!multiSimDiscount,
  };
};

const hasUnlimitedMaxPromo = (mobile: BasketMobile): boolean =>
  mobile.sims.some(
    (sim) =>
      sim.selectedTariff.tariffType === MobileTariffType.UnlimitedMax &&
      (sim.selectedTariff.discounts?.length ?? 0) > 0
  );

const hasMultipleSimsSelected = (mobile: BasketMobile): boolean =>
  mobile.selectedSimCount > 1;

const formatSimName = (name?: string): string => {
  if (!name) return '';

  switch (name) {
    case MOBILE_TARIFF_API_NAMES.ESSENTIAL:
      return MOBILE_TARIFF_NAMES_FORMATTED.ESSENTIAL;
    case MOBILE_TARIFF_API_NAMES.UNLIMITED_PLUS:
      return MOBILE_TARIFF_NAMES_FORMATTED.UNLIMITED_PLUS;
    case MOBILE_TARIFF_API_NAMES.ESSENTIAL_MAX:
      return MOBILE_TARIFF_NAMES_FORMATTED.ESSENTIAL_MAX;
    case MOBILE_TARIFF_API_NAMES.UNLIMITED_MAX:
      return MOBILE_TARIFF_NAMES_FORMATTED.UNLIMITED_MAX;
    case MOBILE_TARIFF_API_NAMES.GO_ESSENTIAL:
      return MOBILE_TARIFF_NAMES_FORMATTED.GO_ESSENTIAL;
    case MOBILE_TARIFF_API_NAMES.GO_UNLIMITED:
      return MOBILE_TARIFF_NAMES_FORMATTED.GO_UNLIMITED;
    default:
      return name;
  }
};

export {
  findMultiSimDiscount,
  findUnlimitedMaxDiscount,
  discountedMobileService,
  discountedSimTariff,
  getDiscountDurationMonths,
  hasUnlimitedMaxPromo,
  hasMultipleSimsSelected,
  formatSimName,
};
